import React from "react";
import { motion } from "framer-motion";
import {
  FiX,
  FiCalendar,
  FiFlag,
  FiUsers,
  FiCheckSquare,
  FiClock,
  FiEdit2,
  FiSave,
} from "react-icons/fi";
import { setDocData } from "../../../utils/firebase/Firebase.utils";
import toast from "react-hot-toast";
import TaskAssignmentModule from "../../../components/TaskAssignmentModule";

export default function TodoDetails({ todo, onClose }) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [formData, setFormData] = React.useState(todo);
  const [assignedToArray, setAssignedToArray] = React.useState(
    todo.assignedTo || []
  );

  const handleStatusChange = async () => {
    const newStatus = formData.status === "completed" ? "pending" : "completed";
    try {
      await setDocData("todos", todo.id, { status: newStatus });
      setFormData({ ...formData, status: newStatus });
      toast.success("Status updated successfully");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSave = async () => {
    try {
      const updatedTodo = {
        ...formData,
        assignedTo: assignedToArray,
        updatedAt: new Date().toISOString(),
      };
      await setDocData("todos", todo.id, updatedTodo);
      toast.success("Task updated successfully");
      setIsEditing(false);
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const priorityOptions = [
    { value: "high", label: "High Priority", icon: "🔴" },
    { value: "medium", label: "Medium Priority", icon: "🟡" },
    { value: "low", label: "Low Priority", icon: "🟢" },
  ];

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden max-w-2xl w-full mx-auto">
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between border-b border-gray-100">
        <div>
          <h2 className="text-lg font-semibold text-[#363062] flex items-center gap-2">
            Task Details
          </h2>
          <p className="text-sm text-gray-500">View and manage task details</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => (isEditing ? handleSave() : setIsEditing(true))}
            className="p-2 rounded-full hover:bg-gray-50 transition-colors text-[#8FCD00] hover:text-[#7baf00]"
          >
            {isEditing ? (
              <FiSave className="w-5 h-5" />
            ) : (
              <FiEdit2 className="w-5 h-5" />
            )}
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-50 transition-colors"
          >
            <FiX className="w-5 h-5 text-gray-400 hover:text-[#363062]" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Task Title and Status */}
        <div className="flex items-start justify-between mb-6">
          {isEditing ? (
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="text-xl font-semibold text-[#363062] bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 w-2/3 focus:outline-none focus:ring-2 focus:ring-[#8FCD00]/20 focus:border-[#8FCD00]"
            />
          ) : (
            <h3 className="text-xl font-semibold text-[#363062]">
              {formData.name}
            </h3>
          )}
          <button
            onClick={handleStatusChange}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              formData.status === "completed"
                ? "bg-green-100 text-green-700 hover:bg-green-200"
                : "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
            }`}
          >
            {formData.status === "completed" ? (
              <FiCheckSquare className="w-4 h-4" />
            ) : (
              <FiClock className="w-4 h-4" />
            )}
            {formData.status === "completed" ? "Completed" : "Pending"}
          </button>
        </div>

        {/* Description */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Description
          </h4>
          {isEditing ? (
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8FCD00]/20 focus:border-[#8FCD00] transition-colors resize-none bg-gray-50"
              placeholder="Enter task description"
            />
          ) : (
            <p className="text-gray-600 bg-gray-50 rounded-lg p-4 border border-gray-100">
              {formData.description || "No description provided"}
            </p>
          )}
        </div>

        {/* Task Details Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* Due Date */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
              <FiCalendar className="w-4 h-4 text-[#8FCD00]" />
              Due Date
            </div>
            {isEditing ? (
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8FCD00]/20 focus:border-[#8FCD00] bg-white"
              />
            ) : (
              <p className="text-gray-600">
                {formData.dueDate || "No due date set"}
              </p>
            )}
          </div>

          {/* Priority */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
              <FiFlag className="w-4 h-4 text-[#8FCD00]" />
              Priority
            </div>
            {isEditing ? (
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8FCD00]/20 focus:border-[#8FCD00] bg-white"
              >
                {priorityOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.icon} {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <div
                className={`inline-flex items-center px-2 py-1 rounded-md text-sm font-medium ${
                  formData.priority === "high"
                    ? "bg-red-100 text-red-700"
                    : formData.priority === "medium"
                    ? "bg-yellow-100 text-yellow-700"
                    : "bg-green-100 text-green-700"
                }`}
              >
                {formData.priority}
              </div>
            )}
          </div>
        </div>

        {/* Assigned Users */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
            <FiUsers className="w-4 h-4 text-[#8FCD00]" />
            Assigned Users
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            {isEditing ? (
              <TaskAssignmentModule
                assignedToArray={assignedToArray}
                setAssignedToArray={setAssignedToArray}
                editMode={true}
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                {(formData.assignedTo || []).map((user, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-gray-200"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                      {user.photoURL ? (
                        <img
                          src={user.photoURL}
                          alt={user.label}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-sm font-medium text-gray-600">
                          {user.label?.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-gray-700">{user.label}</span>
                  </div>
                ))}
                {!formData.assignedTo?.length && (
                  <p className="text-sm text-gray-500">No users assigned</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Creation Info */}
        <div className="text-xs text-gray-500 pt-4 border-t border-gray-100">
          Created {new Date(formData.createdAt).toLocaleString()} by{" "}
          {formData.createdBy}
          {formData.updatedAt && (
            <>
              <br />
              Last updated {new Date(formData.updatedAt).toLocaleString()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
